// Display table card in grid view

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { closeMesa } from '../../../../services/firebase/functions';
import { RefreshCw, Clock, AlertCircle, DollarSign, Users, CheckCircle, XCircle, Wifi, WifiOff, DoorOpen, Pencil, Bell } from 'lucide-react';
import { Mesa, MesaItem } from '../../../../types/mesa';
import { useRestaurant } from '../../../../providers/RestaurantContext';
import { toast } from 'react-toastify';
import { db } from '../../../../config/firebase/config';

// Format currency with thousand separators
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

interface Props {
  mesa: Mesa;
  onSyncFromPOS: (mesaId: string) => void;
  isSyncing?: boolean;
  onRetrySync?: (mesaId: string) => void;
  onMesaClosed?: () => void;
  onMesaUpdated?: () => void;
  unreadNotificationCount?: number;
}

const MesaCard: React.FC<Props> = ({ mesa, onSyncFromPOS, isSyncing = false, onRetrySync, onMesaClosed, onMesaUpdated, unreadNotificationCount = 0 }) => {
  const [closing, setClosing] = useState(false);
  const [editTotalOpen, setEditTotalOpen] = useState(false);
  const [editTotalValue, setEditTotalValue] = useState('');
  const [editTotalSaving, setEditTotalSaving] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState(mesa.numeroMesa || '');
  const [savingName, setSavingName] = useState(false);
  const { restaurant } = useRestaurant();
  // Pagado from Firestore (mesa.montoPagado). Session merges + mirrored mesa doc after payment.
  const pagado = Number(mesa.montoPagado) || 0;
  const taxRate = restaurant?.settings?.taxRate || 18; // Default 18%
  // Total con impuesto: use synced value when present, else compute from items + tax
  const itemSubtotal = (mesa.items ?? []).reduce((s, i) => s + (i.quantity ?? 1) * (i.price ?? 0), 0);
  const total = mesa.montoTotalConImpuestos ?? (itemSubtotal > 0 ? itemSubtotal * (1 + taxRate / 100) : (mesa.montoTotal ?? 0) * (1 + taxRate / 100));
  // Prefer Firestore restante (session/authoritative) when present
  const restanteRaw = (mesa as any).restante;
  const restante =
    typeof restanteRaw === 'number' && !Number.isNaN(restanteRaw)
      ? Math.max(0, restanteRaw)
      : Math.max(0, total - pagado);
  
  // ========== CRITICAL: Check if POS is CONFIGURED ==========
  const restaurantIntegrationMode = restaurant?.integrationMode || 'none';
  const posProvider = restaurant?.posConfig?.provider || 'none';
  const hasPOSConfigured = 
    restaurantIntegrationMode !== 'none' && 
    posProvider !== 'none';
  
  // Check if mesa has new schema fields
  const hasNewSchema = mesa.activeSource !== undefined && mesa.integrationMode !== undefined;
  
  // ========== CRITICAL: Check if ACTUALLY SYNCED (not just configured) ==========
  // A mesa is ACTUALLY synced if and only if:
  // 1. syncStatus === "ok" (backend confirmed successful sync)
  // 2. activeSource !== "manual" (not in fallback mode)
  const isActuallySynced = 
    mesa.syncStatus === 'ok' &&
    mesa.activeSource !== 'manual';
  
  // Manual mode flag (per mesa.integrationMode)
  const isManual = mesa.integrationMode === 'manual';
  
  // ✅ FIXED: Show retry button if restaurant has POS and mesa is NOT actually synced
  // This covers: failed syncs, never-synced mesas, stale syncs
  const canRetrySync = hasPOSConfigured && 
                       !isActuallySynced && 
                       !isManual;
  
  // Check if sync data is stale (older than 5 minutes) - only when actually synced
  const isSyncStale = isActuallySynced && mesa.lastPosSync && typeof mesa.lastPosSync.toDate === 'function'
    ? (Date.now() - mesa.lastPosSync.toDate().getTime()) > 5 * 60 * 1000
    : false;

  // Calculate time elapsed since table was opened
  const getTimeElapsed = () => {
    if (!mesa.ultimaActualizacion) return null;
    const now = Date.now();
    const openedAt = mesa.ultimaActualizacion.toDate().getTime();
    const diffMinutes = Math.floor((now - openedAt) / (1000 * 60));
    
    if (diffMinutes < 60) return `${diffMinutes}m`;
    const hours = Math.floor(diffMinutes / 60);
    const mins = diffMinutes % 60;
    return `${hours}h ${mins}m`;
  };

  const timeElapsed = getTimeElapsed();
  // Manual mode: treat mesa as occupied when status is "ocupada" OR "pending_pos_closure"
  // (pending_pos_closure = fully paid but admin must close it manually — still occupied).
  const OCCUPIED_STATUSES = new Set(['ocupada', 'pending_pos_closure']);
  const isLibre = isManual ? !OCCUPIED_STATUSES.has(mesa.status as string) : !mesa.activeSessionId;

  // Single layout for all cards (same structure as Mesa 1 & 2): header, sync badge, 4 financial rows, optional Cerrar mesa, Detalles + QR
  return (
    <div className="group relative bg-zinc-900/60 rounded-xl border border-white/5 p-3 flex flex-col justify-between min-h-[300px] transition-all duration-200 hover:bg-zinc-800 hover:border-yap-primary/40">
      {/* Title and Status */}
      <div className="flex justify-between items-start mb-2 shrink-0">
        <div className="flex items-center gap-2">
          {isEditingName ? (
            <input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Escape') {
                  setIsEditingName(false)
                  setEditingName(mesa.numeroMesa || '')
                  return
                }
                if (e.key !== 'Enter' || !restaurant?.id || savingName) return
                const nextName = editingName.trim()
                if (!nextName) return
                try {
                  setSavingName(true)
                  await updateDoc(doc(db, 'restaurants', restaurant.id, 'mesas', mesa.id), {
                    numeroMesa: nextName,
                    ultimaActualizacion: serverTimestamp(),
                  })
                  toast.success('Nombre de mesa actualizado')
                  setIsEditingName(false)
                  onMesaUpdated?.()
                } catch (err: any) {
                  toast.error(err?.message || 'No se pudo actualizar el nombre')
                } finally {
                  setSavingName(false)
                }
              }}
              className="px-2 py-1 rounded bg-zinc-800 border border-white/10 text-white text-lg w-24"
              autoFocus
            />
          ) : (
            <>
              <h2 className="text-xl font-semibold text-white tracking-tight">#{mesa.numeroMesa}</h2>
              <button
                type="button"
                onClick={() => {
                  setEditingName(mesa.numeroMesa || '')
                  setIsEditingName(true)
                }}
                className="text-zinc-400 hover:text-white"
                title="Editar nombre de mesa"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-1.5">
            {unreadNotificationCount > 0 && (
              <span className="relative flex items-center justify-center w-5 h-5">
                <Bell className="w-3.5 h-3.5 text-amber-400" />
                <span className="absolute -top-1 -right-1 min-w-[13px] h-[13px] px-0.5 flex items-center justify-center rounded-full bg-amber-500 text-white text-[8px] font-bold leading-none">
                  {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                </span>
              </span>
            )}
            <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
              isLibre
                ? 'bg-zinc-700/60 text-zinc-300 border border-white/5'
                : mesa.estado === 'abierta'
                  ? 'bg-yap-primary/20 text-yap-primary border border-yap-primary/30'
                  : 'bg-zinc-800/60 text-zinc-400 border border-white/5'
            }`}>
              <div className={`w-2 h-2 rounded-full ${isLibre ? 'bg-zinc-400' : mesa.estado === 'abierta' ? 'bg-green-500' : 'bg-red-500'}`} />
              {isLibre ? 'Libre' : mesa.estado === 'abierta' ? 'Ocupada' : 'Cerrada'}
            </span>
          </div>
          {!isLibre && timeElapsed && mesa.estado === 'abierta' && (
            <span className="text-[11px] text-zinc-500 font-medium mt-0.5">{timeElapsed}</span>
          )}
        </div>
      </div>

      {/* Reattachment banner (only when ocupada) */}
      {!isManual && !isLibre && mesa.reattachedFromPOS && (
        <div className="mb-2 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[11px] flex items-center gap-1.5 shrink-0">
          <AlertCircle className="w-3 h-3 shrink-0" />
          <span>El ticket del POS para esta mesa sigue abierto.</span>
        </div>
      )}

      {/* Sync / status badge (hidden in manual mode to keep UI focused) */}
      {!isManual && (restaurantIntegrationMode === 'none' ? (
        // Manual mode — hide POS-specific indicators and retry button
        <div className="flex items-center gap-2 text-[11px] mb-2 pb-2 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isLibre ? (
              <>
                <DoorOpen className="w-3 h-3 text-zinc-500 shrink-0" />
                <span className="text-zinc-400 font-medium">Mesa en modo manual</span>
              </>
            ) : (
              <>
                <DoorOpen className="w-3 h-3 text-zinc-500 shrink-0" />
                <span className="text-zinc-400 font-medium">Modo manual (sin POS)</span>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[11px] mb-2 pb-2 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isLibre ? (
              <>
                <DoorOpen className="w-3 h-3 text-zinc-500 shrink-0" />
                <span className="text-zinc-400 font-medium">Esperando pedido</span>
              </>
            ) : !hasNewSchema ? (
              <>
                <AlertCircle className="w-3 h-3 text-yellow-500 shrink-0" />
                <span className="text-yellow-400 font-medium">Requiere actualización de esquema</span>
              </>
            ) : !hasPOSConfigured ? (
              <>
                <WifiOff className="w-3 h-3 text-zinc-500 shrink-0" />
                <span className="text-zinc-400 font-medium">Modo manual (sin POS configurado)</span>
              </>
            ) : isSyncing ? (
              <>
                <RefreshCw className="w-3 h-3 text-blue-500 animate-spin shrink-0" />
                <span className="text-blue-400 font-medium">Actualizando...</span>
              </>
            ) : isActuallySynced && !isSyncStale ? (
              <>
                <Wifi className="w-3 h-3 text-green-500 shrink-0" />
                <span className="text-green-400 font-medium">Sincronizado con POS</span>
              </>
            ) : isSyncStale ? (
              <>
                <AlertCircle className="w-3 h-3 text-orange-500 shrink-0" />
                <span className="text-orange-400 font-medium">Sincronización desactualizada</span>
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3 text-zinc-500 shrink-0" />
                <span className="text-zinc-400 font-medium">Modo manual activo</span>
              </>
            )}
          </div>
          {!isLibre && canRetrySync && onRetrySync && (
            <button
              onClick={() => onRetrySync(mesa.id)}
              disabled={isSyncing}
              className="p-1 rounded-md text-orange-400 hover:text-orange-300 hover:bg-orange-900/20 transition-colors disabled:opacity-50 shrink-0"
              title="Reintentar sincronización"
            >
              <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      ))}

      {/* Libre: Abrir mesa only. Ocupada: Total, Pagado, Restante, Artículos */}
      {isLibre ? (
        <>
          <button
            type="button"
            className="mb-2 w-full px-2 py-1.5 rounded-lg bg-zinc-700/60 border border-white/10 text-zinc-300 text-xs font-medium hover:bg-zinc-600/60 transition-colors flex items-center justify-center gap-1.5 shrink-0"
            onClick={async () => {
              if (!restaurant?.id) {
                toast.error('No se pudo identificar el restaurante')
                return
              }

              if (!isManual) {
                toast.info('La mesa se abrirá al recibir un pedido desde el POS')
                return
              }

              try {
                // Manual open: new session so QR from previous session becomes invalid.
                // Always start totals at 0 — do not reuse stale montoTotal from the mesa doc (old bills).
                const newSessionId = crypto.randomUUID()
                console.log("SESSION_CREATED", mesa.id, newSessionId)
                const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesa.id)
                await updateDoc(mesaRef, {
                  status: 'ocupada',
                  estado: 'abierta',
                  activeSessionId: newSessionId,
                  montoTotal: 0,
                  montoTotalConImpuestos: 0,
                  montoPagado: 0,
                  restante: 0,
                  items: [],
                  ultimaActualizacion: serverTimestamp(),
                })
                // Mirror bill on session subdoc so payments & admin session merge work
                await setDoc(doc(mesaRef, 'sessions', newSessionId), {
                  posTicketId: `manual-${mesa.id}`,
                  estado: 'abierta',
                  items: [],
                  montoTotal: 0,
                  montoTotalConImpuestos: 0,
                  montoPagado: 0,
                  restante: 0,
                  createdAt: serverTimestamp(),
                  closedBy: null,
                })

                // Waiter sets the real total with "Editar total" (or Detalles).
                setEditTotalOpen(false)
                onMesaUpdated?.()
                toast.success('Mesa abierta. Usa «Editar total» para ingresar la cuenta.')
              } catch (e: any) {
                console.error('Error opening mesa in manual mode:', e)
                toast.error(e?.message || 'Error al abrir la mesa')
              }
            }}
          >
            <DoorOpen className="w-3.5 h-3.5" />
            Abrir mesa
          </button>
        </>
      ) : (
        <div className="space-y-1.5 mb-3 min-h-[132px] flex flex-col justify-start shrink-0">
          <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
            <span className="text-zinc-400 font-medium">{isManual ? 'Total' : 'Total con impuesto'}</span>
            <span className="text-yap-primary font-semibold">RD${formatCurrency(total)}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
            <span className="text-zinc-400 font-medium">Pagado</span>
            <span className="text-green-400 font-semibold">RD${formatCurrency(pagado)}</span>
          </div>
          {Number((mesa as any).tipTotal) > 0 && (
            <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
              <span className="text-zinc-400 font-medium">Propinas</span>
              <span className="text-amber-400 font-semibold">RD${formatCurrency(Number((mesa as any).tipTotal))}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
            <span className="text-zinc-400 font-medium">Restante</span>
            <span className="text-red-400 font-semibold">RD${formatCurrency(restante)}</span>
          </div>
          {!isManual && (
            <div className="flex items-center justify-between text-[11px] p-2 bg-zinc-800/40 rounded-md">
              <span className="text-zinc-400 font-medium">Artículos</span>
              <span className="text-white font-semibold">{mesa.items?.length ?? 0}</span>
            </div>
          )}
        </div>
      )}

      {/* Manual: cerrar mesa en cualquier momento (liberar mesa). Con saldo pendiente pide confirmación. */}
      {!isLibre && isManual && (
        <button
          type="button"
          disabled={closing || !restaurant?.id}
          onClick={async () => {
            if (!restaurant?.id) return;
            const hasPending = restante > 0.01;
            const hadPayments = pagado > 0.01;
            if (hasPending) {
              const msg = hadPayments
                ? `Esta mesa tiene saldo pendiente (RD$${formatCurrency(restante)}). ¿Cerrar de todos modos? Se guardará el resumen en pagos y la mesa quedará libre.`
                : `¿Cerrar la mesa? No hay pagos registrados en la cuenta. La mesa quedará libre.`;
              const ok = window.confirm(msg);
              if (!ok) return;
            }
            setClosing(true);
            try {
              await closeMesa({
                restaurantId: restaurant.id,
                mesaId: mesa.id,
                forceManualEarlyClose: hasPending,
              });
              toast.success(
                !hadPayments
                  ? 'Mesa cerrada. La mesa quedó libre.'
                  : hasPending
                    ? 'Mesa cerrada (saldo pendiente). Resumen en pagos.'
                    : 'Mesa cerrada. Detalles guardados en pagos.'
              );
              onMesaClosed?.();
            } catch (e: any) {
              const msg = e?.message || e?.code || 'Error al cerrar la mesa';
              toast.error(msg);
            } finally {
              setClosing(false);
            }
          }}
          className="mb-2 w-full px-2 py-1.5 rounded-lg bg-zinc-700/80 border border-white/10 text-zinc-200 text-xs font-medium hover:bg-zinc-600 hover:text-white transition-colors disabled:opacity-50 shrink-0"
        >
          {closing ? 'Cerrando…' : 'Cerrar mesa'}
        </button>
      )}

      {/* POS: cerrar solo cuando cuenta pagada */}
      {!isLibre && !isManual && (restante <= 0.01 || mesa.estado === 'cerrada') && total > 0 && (
        <button
          type="button"
          disabled={closing || !restaurant?.id}
          onClick={async () => {
            if (!restaurant?.id) return;
            setClosing(true);
            try {
              await closeMesa({ restaurantId: restaurant.id, mesaId: mesa.id });
              toast.success('Mesa cerrada. Detalles guardados en pagos.');
              onMesaClosed?.();
            } catch (e: any) {
              const msg = e?.message || e?.code || 'Error al cerrar la mesa';
              toast.error(msg);
            } finally {
              setClosing(false);
            }
          }}
          className="mb-2 w-full px-2 py-1.5 rounded-lg bg-yap-primary/15 border border-yap-primary/30 text-yap-primary text-xs font-medium hover:bg-yap-primary/25 transition-colors disabled:opacity-50 shrink-0"
        >
          {closing ? 'Cerrando…' : 'Cerrar mesa'}
        </button>
      )}

      {/* Actions: manual mode only shows totals + QR */}
      <div className="space-y-1 mt-auto shrink-0">
        {isManual ? (
          <>
            {!isLibre && (
              <Link to={`/admin/mesa/${mesa.id}/resumen`} className="block">
                <button className="w-full py-1.5 text-[11px] font-semibold text-zinc-200 bg-zinc-700/90 rounded-md hover:text-white hover:bg-zinc-600 transition-colors">
                  Detalles
                </button>
              </Link>
            )}
            {!isLibre && pagado === 0 && (
              <button
                type="button"
                onClick={() => {
                  setEditTotalValue(String(mesa.montoTotal ?? 0));
                  setEditTotalOpen(true);
                }}
                className="w-full py-1.5 text-[11px] font-semibold text-zinc-300 bg-zinc-800/80 rounded-md hover:text-white hover:bg-zinc-700 transition-colors"
              >
                Editar total
              </button>
            )}
            <Link to={`/admin/mesa/${mesa.id}/qr`} className="block">
              <button className="w-full py-1.5 text-[11px] font-semibold text-black bg-yap-primary rounded-md hover:bg-yap-hover transition-all shadow-[0_0_10px_-3px_rgba(0,255,132,0.3)] hover:scale-[1.01] active:scale-95">
                QR
              </button>
            </Link>
          </>
        ) : (
          <>
            <Link to={`/admin/mesa/${mesa.id}/resumen`} className="block">
              <button className="w-full py-1.5 text-[11px] font-semibold text-zinc-300 bg-zinc-800/80 rounded-md hover:text-white hover:bg-zinc-700 transition-colors">
                Detalles
              </button>
            </Link>
            <Link to={`/admin/mesa/${mesa.id}/qr`} className="block">
              <button className="w-full py-1.5 text-[11px] font-semibold text-black bg-yap-primary rounded-md hover:bg-yap-hover transition-all shadow-[0_0_10px_-3px_rgba(0,255,132,0.3)] hover:scale-[1.01] active:scale-95">
                QR
              </button>
            </Link>
          </>
        )}
      </div>

      {/* Last Payment - only when ocupada */}
      {!isLibre && mesa.lastPaymentAt && (
        <div className="text-[10px] text-zinc-600 text-center mt-1.5 shrink-0">
          Último pago: {mesa.lastPaymentAt.toDate().toLocaleString('es-DO', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>
      )}

      {/* Edit total modal (manual mode only) */}
      {editTotalOpen && isManual && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6 max-w-md w-full shadow-2xl animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Editar total de la cuenta</h2>
              <button
                onClick={() => setEditTotalOpen(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                disabled={editTotalSaving}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-zinc-300">
                Total (RD$)
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={editTotalValue}
                onChange={(e) => setEditTotalValue(e.target.value)}
                placeholder="Ej: 850.00"
                className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-yap-primary focus:border-transparent"
                disabled={editTotalSaving}
                autoFocus
              />
              <p className="text-xs text-zinc-500">
                Solo se permite editar el total antes de cualquier pago.
              </p>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditTotalOpen(false)}
                  disabled={editTotalSaving}
                  className="flex-1 px-4 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!restaurant?.id) {
                      toast.error('No se pudo identificar el restaurante');
                      return;
                    }
                    if (mesa.montoPagado > 0) {
                      toast.error('No puedes editar el total después de que exista un pago');
                      return;
                    }

                    const parsedTotal = parseFloat(editTotalValue.replace(',', '.'));
                    if (isNaN(parsedTotal) || parsedTotal <= 0) {
                      toast.error('Ingresa un total válido');
                      return;
                    }

                    try {
                      setEditTotalSaving(true);

                      const montoPagadoActual = mesa.montoPagado ?? 0;
                      const restanteCalculado = Math.max(0, parsedTotal - montoPagadoActual);
                      // Manual mode totals are stored on the mesa doc.
                      const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesa.id);
                      await updateDoc(mesaRef, {
                        montoTotal: parsedTotal,
                        montoTotalConImpuestos: parsedTotal,
                        montoPagado: montoPagadoActual,
                        restante: restanteCalculado,
                        estado: 'abierta',
                        ultimaActualizacion: serverTimestamp(),
                      });

                      const sid = typeof mesa.activeSessionId === 'string' ? mesa.activeSessionId : null;
                      if (sid) {
                        const sessionRef = doc(mesaRef, 'sessions', sid);
                        const sessionSnap = await getDoc(sessionRef);
                        if (sessionSnap.exists()) {
                          await updateDoc(sessionRef, {
                            montoTotal: parsedTotal,
                            montoTotalConImpuestos: parsedTotal,
                            montoPagado: montoPagadoActual,
                            restante: restanteCalculado,
                            estado: 'abierta',
                          });
                        } else {
                          // Mesa tiene activeSessionId pero el subdoc nunca se creó (mesas antiguas)
                          await setDoc(sessionRef, {
                            posTicketId: `manual-${mesa.id}`,
                            estado: 'abierta',
                            items: [],
                            montoTotal: parsedTotal,
                            montoTotalConImpuestos: parsedTotal,
                            montoPagado: montoPagadoActual,
                            restante: restanteCalculado,
                            createdAt: serverTimestamp(),
                            closedBy: null,
                          });
                        }
                      }

                      toast.success('Total actualizado correctamente');
                      setEditTotalOpen(false);
                      onMesaUpdated?.();
                    } catch (e: any) {
                      console.error('Error updating mesa total:', e);
                      toast.error(e?.message || 'Error al actualizar el total');
                    } finally {
                      setEditTotalSaving(false);
                    }
                  }}
                  disabled={editTotalSaving}
                  className="flex-1 px-4 py-3 bg-yap-primary text-black rounded-lg hover:bg-yap-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_-5px_rgba(0,255,132,0.3)]"
                >
                  {editTotalSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MesaCard;

